import React, { useEffect } from 'react';
import { useState } from 'react';
import { createNextState } from '@reduxjs/toolkit';
import {
  BundleSearchRequest,
  CreateBundleRequest,
  useCreateBundleMutation,
  useSearchBundlesQuery,
} from '../../../redux/api/bundles';
import { BundlesTable } from './BundlesTable';
import { useSelector } from 'react-redux';
import { selectAuthUserInfo } from '../../../redux/auth/selectors';
import { useIntl } from 'react-intl';
import { BundleMessages } from '../../../intl/BundleMessages';
import { Roles } from '@dataquiver/stoneage-roles';
import { useSocketList } from '../../../utils/socket';
import { SocketContextPortal } from '../../../utils/socket/context/portalSocketContext';
import { BundleListPageActions } from '../../../pages/bundles/BundlesListPage';
import { useHistory, useLocation } from 'react-router';
import { ListOrganizationsRequest } from '../../../models/organization';
import { useToast } from 'src/components/reusable-components/useToast';

interface BundlesTableCardProps {
  setToast: (success: boolean, message: string, header?: string) => void;
}

export const BundlesTableCard: React.FC<BundlesTableCardProps> = ({ setToast }) => {
  const user = useSelector(selectAuthUserInfo);
  const { formatMessage } = useIntl();
  const history = useHistory();
  const [present] = useToast();
  const { state: locationState } = useLocation<{ action?: BundleListPageActions }>();
  const [bundlesRequest, setBundlesRequest] = useState<BundleSearchRequest>({
    pageNumber: 0,
    pageSize: 10,
    searchTerm: undefined,
    organizationId: Roles.isStoneAgeDev(user?.role) ? undefined : user?.org,
    sort: { order: 'desc', property: 'createdAt' },
  });

  const { data: bundlesResults, refetch } = useSearchBundlesQuery(bundlesRequest);

  const [orgRequest] = useState<ListOrganizationsRequest>({
    statuses: ['active'],
    pageNumber: 0,
    pageSize: 1000,
    sort: { order: 'asc', property: 'orgName' },
  });
  const { items: organizations } = useSocketList(SocketContextPortal, 'organizations', orgRequest, 'listV2');

  const handleQueryChange = React.useCallback((requestUpdates: Partial<BundleSearchRequest>) => {
    setBundlesRequest(
      createNextState(draft => {
        Object.assign(draft, requestUpdates);
      }),
    );
  }, []);

  const [createBundle] = useCreateBundleMutation();

  const onCreateBundle = React.useCallback(
    async (request: CreateBundleRequest) => {
      try {
        const result = await createBundle(request).unwrap();
        refetch();
        setToast(
          true,
          formatMessage(BundleMessages.bundleCreatedSuccessfullyMessage),
          formatMessage(BundleMessages.bundleCreatedSuccessfullyHeader),
        );

        history.push(`bundles/${result._id}`);
      } catch (err) {
        console.error('Error creating bundle: ', err);
        setToast(
          false,
          formatMessage(BundleMessages.bundleCreateFailedMessage),
          formatMessage(BundleMessages.bundleCreateFailedTitle),
        );
      }
    },
    [createBundle, formatMessage, history, refetch, setToast],
  );

  // Show deleted successful or failed toast if the action occurred.
  useEffect(() => {
    if (locationState?.action === BundleListPageActions.BundleDeletedSuccessful) {
      history.replace({ state: {} });

      present(
        {
          message: formatMessage(BundleMessages.bundleDeletedSuccessfullyMessage),
        },
        'success',
      );
    } else if (locationState?.action === BundleListPageActions.BundleDeleteFailed) {
      history.replace({ state: {} });
      present(
        {
          message: formatMessage(BundleMessages.bundleDeletionFailedMessage),
        },
        'danger',
      );
    }
  }, [user?.org, user?.role, formatMessage, history, locationState?.action, present]);

  return (
    <BundlesTable
      organizations={organizations}
      data={bundlesResults ?? { results: [], totalCount: 0 }}
      request={bundlesRequest}
      onTableChange={handleQueryChange}
      onCreateBundle={onCreateBundle}
    />
  );
};
